// Core packages
import { formatDate } from "@angular/common";
import {
  AfterViewInit, Component, OnDestroy, OnInit, ViewChild
} from "@angular/core";
import { FormControl } from "@angular/forms";

// Third party packages
import { MatDialog, MatOptionSelectionChange } from "@angular/material";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { CookieService } from "ngx-cookie-service";
import { combineLatest, merge, Subscription } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged, finalize, startWith, tap
} from "rxjs/operators";

// Core packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { ListSetting } from "src/app/shared/interfaces/list-setting.interface";
import { ICompany } from "src/app/shared/models/company/company.interface";
import { IUser } from "src/app/shared/models/user/user.interface";
import { User } from "src/app/shared/models/user/user.model";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ListsService } from "src/app/shared/services/lists.service";

import { PersonalDataService } from "src/app/modules/personal-data/personal-data.service";
import { PersonalDataSource } from "src/app/shared/utils/data-sources/personal-data-source";

  
@Component({
  selector: "app-personal-data-list",
  templateUrl: "./personal-data-list.page.html",
  styleUrls: ["./personal-data-list.page.scss"],
})
export class PersonalDataListComponent
  implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  // BEGIN - Table 
  displayedColumns: string[] = [
    "index",
    "master",
    "name",
    "masterCode",
    "address",
    "taxCode",
    // "iban" column is intentionally hidden (matColumnDef kept in template): add it back here to show it again
    "administrationStartDate",
    "administrationEndDate",
    "actions",
  ];
  dataSource: PersonalDataSource;
  @ViewChild("sortTable", { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  adminFilter: FormControl = new FormControl("");
  nameFilter: FormControl = new FormControl("");
  masterCodeFilter: FormControl = new FormControl("");
  addressFilter: FormControl = new FormControl("");
  taxCodeFilter: FormControl = new FormControl("");
  ibanFilter: FormControl = new FormControl("");
  administrationStartDateFilter: FormControl = new FormControl("");
  administrationEndDateFilter: FormControl = new FormControl("");
  // END - Table 

  // BEGIN - Autocomplete Master fields data
  adminIsLoading = false;
  availableAdmins: User[] = [];
  // END - Autocomplete Master fields data

  loggedUserRoleType = "";

  /**
   * Class constructor
   */
  constructor(
    private personalDataService: PersonalDataService,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private listsService: ListsService,
    private apiService: ApiService,
    private configService: ConfigService,
    private cookieService: CookieService,
    public confirmDialog: MatDialog,
    private companiesService: CompaniesService,

    public dialog: MatDialog
  ) { }

  
  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );
    this.authService.loggedUserRequestedRoleType$.subscribe((res) => {
      if (res) {
        this.loggedUserRoleType = res;
        if (
          this.loggedUserRoleType === "master" ||
          this.loggedUserRoleType === "collaborator"
        ) {
          const index = this.displayedColumns.findIndex((el: any) => {
            return el === "master";
          });
          this.displayedColumns.splice(index, 1);
        }
        this.subscriptions.push(
          this.companiesService
            .shouldShowMasterCodeColumn$(this.loggedUserRoleType, this.cookieService.get("requestedCompany"))
            .subscribe((show) => {
              if (!show) {
                const masterCodeIndex = this.displayedColumns.findIndex((el: any) => {
                  return el === "masterCode";
                });
                if (masterCodeIndex > -1) {
                  this.displayedColumns.splice(masterCodeIndex, 1);
                }
              }
            })
        );
      }
    });

    // Init data source
    this.dataSource = new PersonalDataSource(
      this.personalDataService,
      this.helperService
    );

    // Subscribe to listsService and update this list settings
    this.subscribeToListsSettings("personalData");

    // Get available admin and owners from users' list
    this.getAvailableAdminsAndOwners();
  }
  
  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0))
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => {
            this.loadPage();
          })
        )
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.adminFilter.valueChanges.pipe(startWith(null as string)),
        this.nameFilter.valueChanges.pipe(startWith(null as string)),
        this.masterCodeFilter.valueChanges.pipe(startWith(null as string)),
        this.addressFilter.valueChanges.pipe(startWith(null as string)),
        this.taxCodeFilter.valueChanges.pipe(startWith(null as string)),
        this.ibanFilter.valueChanges.pipe(startWith(null as string)),
        this.administrationStartDateFilter.valueChanges.pipe(startWith(null as string)),
        this.administrationEndDateFilter.valueChanges.pipe(
          startWith(null as string)
        ),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged() // Eliminate duplicate values
        )
        .subscribe((res) => {
          // Handle admin filter
          if (res[0]) {
            // Since we cannot search in the same time for both admin and
            // owner, we must reset admin field. Maybe this should be handled in a better way
            this.adminIsLoading = true;
            this.getAvailableAdminsAndOwners("admin", {
              general: this.adminFilter.value._id ? this.adminFilter.value._id : this.adminFilter.value,
            });
            return;
          }

          // Load page only if at least one value is not null
          if (!res.every((value) => value === null)) {
            this.paginator.pageIndex = 0;
            this.loadPage();
          }
        })
    );
  }
  
  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());

    // Push current list settings to the listsService
    if (this.paginator) {
      this.listsService.setListSetting(
        "personalData",
        this.paginator.pageIndex.valueOf(),
        this.paginator.pageSize.valueOf(),
        {
          general: "", // this.input.nativeElement.value,
          master: this.adminFilter.value,
          name: this.nameFilter.value,
          masterCode: this.masterCodeFilter.value,
          address: this.addressFilter.value,
          taxCode: this.taxCodeFilter.value,
          iban: this.ibanFilter.value,
          administrationStartDate: this.administrationStartDateFilter.value,
          administrationEndDate: this.administrationEndDateFilter.value,
        }
      );
    }
  }
  
  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: "", // this.input.nativeElement.value,
      master: this.adminFilter.value ? this.adminFilter.value._id : undefined,
      name: this.nameFilter.value,
      masterCode: this.masterCodeFilter.value,
      address: this.addressFilter.value,
      taxCode: this.taxCodeFilter.value,
      iban: this.getIbanSearchValue(),
      administrationStartDate: this.administrationStartDateFilter.value,
      administrationEndDate: this.administrationEndDateFilter.value,
    };
    this.dataSource.loadItems(
      start,
      length,
      this.sort.active,
      this.sort.direction,
      search
    );
  }

  /**
   * Return the IBAN filter value only when the IBAN column is displayed,
   * so that a stale (restored) value cannot apply an invisible filter
   *
   * @since 3.0.0
   */
  private getIbanSearchValue(): string | undefined {
    return this.displayedColumns.indexOf("iban") > -1 ? this.ibanFilter.value : undefined;
  }

  /**
   * Export data in a file with given format
   *
   * @since 1.0.0
   */
  onExport(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: "", // this.input.nativeElement.value,
      master: this.adminFilter.value ? this.adminFilter.value._id : undefined,
      name: this.nameFilter.value,
      masterCode: this.masterCodeFilter.value,
      address: this.addressFilter.value,
      taxCode: this.taxCodeFilter.value,
      iban: this.getIbanSearchValue(),
      administrationStartDate: this.administrationStartDateFilter.value,
      administrationEndDate: this.administrationEndDateFilter.value,
    };

    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      "personalData.exportFileName"
    )} ${date}`;
    this.subscriptions.push(
      this.personalDataService.getExport(
        start,
        length,
        this.sort.active,
        this.sort.direction,
        search
      ).subscribe(
        (res: any) => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: "application/vnd.ms-excel",
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        (err: any) => {
          this.helperService.handleError(err);
        }
      )
    );
  }
  
  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting("", 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = "";
    this.adminFilter.setValue(null);
    this.nameFilter.setValue(null);
    this.masterCodeFilter.setValue(null);
    this.addressFilter.setValue(null);
    this.taxCodeFilter.setValue(null);
    this.ibanFilter.setValue(null);
    this.administrationStartDateFilter.setValue(null);
    this.administrationEndDateFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = "desc";
    this.sort.active = "master";
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe((settings: ListSetting[]) => {
        if (settings.length) {
          settings.forEach((setting: ListSetting) => {
            if (setting.name === name) {
              if (setting.pageIndex) {
                this.paginator.pageIndex = setting.pageIndex;
              }
              if (setting.pageSize) {
                this.paginator.pageSize = setting.pageSize;
              }
              if (setting.search && setting.search.general) {
                // this.input.nativeElement.value = setting.search.general;
              }
              if (setting.search && setting.search.admin) {
                this.adminFilter.setValue(setting.search.admin);
              }
              if (setting.search && setting.search.owner) {
                this.nameFilter.setValue(setting.search.owner);
              }
              if (setting.search && setting.search.masterCode) {
                this.masterCodeFilter.setValue(setting.search.masterCode);
              }
              if (setting.search && setting.search.address) {
                this.addressFilter.setValue(setting.search.address);
              }
              if (setting.search && setting.search.taxCode) {
                this.taxCodeFilter.setValue(setting.search.taxCode);
              }
              if (setting.search && setting.search.iban) {
                this.ibanFilter.setValue(setting.search.iban);
              }
              if (setting.search && setting.search.administrationStartDate) {
                this.administrationStartDateFilter.setValue(setting.search.administrationStartDate);
              }
              if (setting.search && setting.search.administrationEndDate) {
                this.administrationEndDateFilter.setValue(
                  setting.search.administrationEndDate
                );
              }
            }
          });
        }
      })
    );
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = "";
    const event = new KeyboardEvent("keyup");
    // this.input.nativeElement.dispatchEvent(event);
  }

  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
  displayAdminOrOwnerFn(arg?: ICompany): string | undefined {
    return arg ? `${arg.name}` : undefined;
  }

  /**
   * Get the list of available admins and owners
   * used to allow filtering on owner and admin columns
   *
   * @since 1.0.0
   */
  getAvailableAdminsAndOwners(type?: string, search?: any): void {
    this.subscriptions.push(
      this.companiesService
        .getList(0, 10, null, null, search)
        .pipe(
          finalize(() => {
            this.adminIsLoading = false;
          })
        )
        .subscribe((res) => {
          if (res.status) {
            this.availableAdmins = res.data.items;
          }
        })
    );
  }

  /**
   * Handle click on a selected admin in the mat-autocomplete list and fires the loadPage()
   *
   * @since 1.0.0
   */
  onAdminFilterSelected(event: MatOptionSelectionChange): void {
    const value = event.source.value;
    this.adminFilter.setValue(value, { emitEvent: false });
    this.loadPage();
  }
}
